/**
 * Course Manager Module
 * Quản lý khóa học (add, remove, change variation)
 */

import * as StateManager from '../shared/state-manager.js';
import { findLowestPriceVariation, findVariationByName } from '../shared/utils.js';
import { saveCoursesToStorage, clearSubmittedHash } from '../shared/storage-service.js';
import { ensureVariations, ensureBatch } from '../shared/variation-service.js';

// ✅ FREE BASIC COURSES: Auto-add based on selected courses
const FREE_COURSE_GENERAL = 9330;  // Khóa học nguyên lý kế toán doanh nghiệp
const FREE_COURSE_HCSN = 36697;    // Khóa học nguyên lý kế toán hành chính sự nghiệp Thông tư 24

// Import will be injected by main
let renderSelectedCourses, updateTotals, refreshDropdown;

export function injectDependencies(deps) {
    renderSelectedCourses = deps.renderSelectedCourses;
    updateTotals = deps.updateTotals;
    refreshDropdown = deps.refreshDropdown;
}

/**
 * ✅ FREE COURSE LOGIC: Determine which free basic courses should be added
 * based on selected courses
 *
 * @return array Array of Product IDs to add (can be 0, 1, or 2 courses)
 */
function determineFreeBasicCourse() {
    const selectedCourses = StateManager.getSelectedCourses();

    // Get all non-free paid courses (exclude the free courses themselves)
    const paidCourses = selectedCourses.filter(course => {
        return course.product_id !== FREE_COURSE_GENERAL &&
               course.product_id !== FREE_COURSE_HCSN;
    });

    // If no paid courses selected, return empty array
    if (paidCourses.length === 0) {
        return [];
    }

    // Check if any paid course title contains "hành chính sự nghiệp" or "ngân sách xã phường" (case-insensitive)
    const hasHCSN = paidCourses.some(course => {
        const courseName = course.name.toLowerCase();
        return courseName.includes('hành chính sự nghiệp') ||
               courseName.includes('ngân sách xã phường');
    });

    // Check if any paid course does NOT contain "hành chính sự nghiệp" or "ngân sách xã phường"
    const hasNonHCSN = paidCourses.some(course => {
        const courseName = course.name.toLowerCase();
        return !courseName.includes('hành chính sự nghiệp') &&
               !courseName.includes('ngân sách xã phường');
    });

    const result = [];

    // Add GENERAL course if there are non-HCSN courses
    if (hasNonHCSN) {
        result.push(FREE_COURSE_GENERAL);
    }

    // Add HCSN course if there are HCSN courses
    if (hasHCSN) {
        result.push(FREE_COURSE_HCSN);
    }

    return result;
}

/**
 * ✅ FREE COURSE LOGIC: Auto-manage free basic courses
 * This function adds/removes/switches free courses based on selected paid courses
 */
async function autoManageFreeBasicCourse() {
    const targetFreeCourses = determineFreeBasicCourse(); // Now returns array
    const selectedCourses = StateManager.getSelectedCourses();

    // Get currently selected free courses
    const currentFreeGeneral = selectedCourses.find(c => c.product_id === FREE_COURSE_GENERAL);
    const currentFreeHCSN = selectedCourses.find(c => c.product_id === FREE_COURSE_HCSN);

    // Case 1: No free courses needed (no paid courses)
    if (targetFreeCourses.length === 0) {
        // Remove any existing free courses
        const filtered = selectedCourses.filter(course => {
            return course.product_id !== FREE_COURSE_GENERAL &&
                   course.product_id !== FREE_COURSE_HCSN;
        });
        StateManager.setSelectedCourses(filtered);
        return;
    }

    // Case 2: Manage GENERAL course
    if (targetFreeCourses.includes(FREE_COURSE_GENERAL)) {
        // Add GENERAL if not exists
        if (!currentFreeGeneral) {
            await addCourseToSelectionInternal(FREE_COURSE_GENERAL, 0, true);
        }
    } else {
        // Remove GENERAL if not needed
        if (currentFreeGeneral) {
            const filtered = selectedCourses.filter(c => c.product_id !== FREE_COURSE_GENERAL);
            StateManager.setSelectedCourses(filtered);
        }
    }

    // Case 3: Manage HCSN course
    if (targetFreeCourses.includes(FREE_COURSE_HCSN)) {
        // Add HCSN if not exists
        if (!currentFreeHCSN) {
            await addCourseToSelectionInternal(FREE_COURSE_HCSN, 0, true);
        }
    } else {
        // Remove HCSN if not needed
        if (currentFreeHCSN) {
            const filtered = selectedCourses.filter(c => c.product_id !== FREE_COURSE_HCSN);
            StateManager.setSelectedCourses(filtered);
        }
    }
}

/**
 * ✅ INTERNAL: Add course without triggering auto-manage (to prevent recursion)
 * This is used by autoManageFreeBasicCourse() to add free courses
 */
async function addCourseToSelectionInternal(productId, variationId = 0, silent = false) {
    if (!productId) {
        return;
    }

    const selectedCourses = StateManager.getSelectedCourses();
    const coursesData = StateManager.getCoursesData();

    // Skip if already selected
    if (selectedCourses.find(course => course.product_id === productId)) {
        return;
    }

    const course = coursesData.find(item => item.id === productId);
    if (!course) {
        if (!silent) {
            console.warn('Không tìm thấy khóa học ID: ' + productId);
        }
        return;
    }

    const courseItem = {
        product_id: course.id,
        name: course.name,
        type: course.type,
        price: course.price,
        regular_price: course.regular_price,
        sale_price: course.sale_price,
        variations: course.variations || [],
        selected_variation: null,
        final_price: parseFloat(course.price) || 0,
    };

    // Lazy-load variations if missing or missing descriptions
    const needsHydration = courseItem.type === 'variable' && (
        !courseItem.variations ||
        courseItem.variations.length === 0 ||
        courseItem.variations.some(v => !v.description || v.description.trim() === '')
    );

    if (needsHydration) {
        const loaded = await ensureVariations(productId);
        courseItem.variations = loaded;
    }

    if (courseItem.type === 'variable' && courseItem.variations.length > 0) {
        let selectedVar = null;
        if (variationId) {
            selectedVar = courseItem.variations.find(variation => parseInt(variation.variation_id, 10) === variationId);
        }
        if (!selectedVar) {
            // ✅ FREE COURSES: Select "TỰ HỌC" variation for free courses
            const isFreeCourse = (productId === FREE_COURSE_GENERAL || productId === FREE_COURSE_HCSN);
            if (isFreeCourse) {
                selectedVar = findVariationByName(courseItem.variations, 'tự học');
            } else {
                selectedVar = findLowestPriceVariation(courseItem.variations);
            }
        }
        courseItem.selected_variation = selectedVar;
        courseItem.final_price = parseFloat(selectedVar.price) || 0;
    }

    // ✅ FREE COURSES: Mark as free course (keep real price for display, will be discounted)
    if (productId === FREE_COURSE_GENERAL || productId === FREE_COURSE_HCSN) {
        courseItem.isFreeCourse = true;
    }

    selectedCourses.push(courseItem);
}

/**
 * Add WooCommerce product to local cart panel
 */
export async function addCourseToSelection(productId, variationId = 0, silent = false) {
    if (!productId) {
        return;
    }

    const selectedCourses = StateManager.getSelectedCourses();
    const coursesData = StateManager.getCoursesData();

    // Skip if already selected
    if (selectedCourses.find(course => course.product_id === productId)) {
        return;
    }

    const course = coursesData.find(item => item.id === productId);
    if (!course) {
        if (!silent) {
            alert('Không tìm thấy khóa học.');
        }
        return;
    }

    const courseItem = {
        product_id: course.id,
        name: course.name,
        type: course.type,
        price: course.price,
        regular_price: course.regular_price,
        sale_price: course.sale_price,
        variations: course.variations || [],
        selected_variation: null,
        final_price: parseFloat(course.price) || 0,
    };

    // Lazy-load variations if missing
    const needsHydration = courseItem.type === 'variable' && (
        !courseItem.variations ||
        courseItem.variations.length === 0 ||
        courseItem.variations.some(v => !v.description || v.description.trim() === '')
    );

    if (needsHydration) {
        const loaded = await ensureVariations(productId);
        courseItem.variations = loaded;
    }

    if (courseItem.type === 'variable' && courseItem.variations.length > 0) {
        let selectedVar = null;
        if (variationId) {
            selectedVar = courseItem.variations.find(variation => parseInt(variation.variation_id, 10) === variationId);
        }
        if (!selectedVar) {
            // ✅ FREE COURSES: Select "TỰ HỌC" variation for free courses
            const isFreeCourse = (productId === FREE_COURSE_GENERAL || productId === FREE_COURSE_HCSN);
            if (isFreeCourse) {
                selectedVar = findVariationByName(courseItem.variations, 'tự học');
            } else {
                // Select variation with lowest price instead of first one
                selectedVar = findLowestPriceVariation(courseItem.variations);
            }
        }
        courseItem.selected_variation = selectedVar;
        courseItem.final_price = parseFloat(selectedVar.price) || 0;
    }

    // ✅ FREE COURSES: Mark as free course (keep real price for display, will be discounted)
    if (productId === FREE_COURSE_GENERAL || productId === FREE_COURSE_HCSN) {
        courseItem.isFreeCourse = true;
    }

    selectedCourses.push(courseItem);

    // ✅ AUTO-MANAGE FREE COURSES: Check if we need to add/remove/switch free basic courses
    await autoManageFreeBasicCourse();

    renderSelectedCourses();
    updateTotals();
    saveCoursesToStorage();
    refreshDropdown();
    clearSubmittedHash();

    setTimeout(() => {
        const block = jQuery('#ktvhvn-selected-courses');
        if (block.length) {
            jQuery('html, body').animate({
                scrollTop: block.offset().top - 80
            }, 400);
        }
    }, 200);
}

/**
 * Change selected variation for a given course
 */
export function changeVariation(index, variationId) {
    const selectedCourses = StateManager.getSelectedCourses();

    if (!selectedCourses[index] || !selectedCourses[index].variations.length) {
        return;
    }

    const variation = selectedCourses[index].variations.find(item => parseInt(item.variation_id, 10) === variationId);
    if (!variation) {
        return;
    }

    selectedCourses[index].selected_variation = variation;
    selectedCourses[index].final_price = parseFloat(variation.price) || 0;

    renderSelectedCourses();
    updateTotals();
    saveCoursesToStorage();
    clearSubmittedHash();
}

/**
 * Remove course
 */
export async function removeCourse(index) {
    const selectedCourses = StateManager.getSelectedCourses();

    if (typeof selectedCourses[index] === 'undefined') {
        return;
    }

    selectedCourses.splice(index, 1);

    // ✅ AUTO-MANAGE FREE COURSES: Check if we need to add/remove/switch free basic courses
    await autoManageFreeBasicCourse();

    renderSelectedCourses();
    updateTotals();
    saveCoursesToStorage();
    refreshDropdown();
    clearSubmittedHash();
}

/**
 * Auto add recently viewed products to selection
 * ✅ OPTIMIZED: Add all products first, then manage free courses once
 */
export async function autoAddRecentlyViewed() {
    const recentlyViewedData = StateManager.getRecentlyViewedData();
    const selectedCourses = StateManager.getSelectedCourses();

    if (!recentlyViewedData || recentlyViewedData.length === 0) {
        return;
    }

    console.log('Auto-adding recently viewed products:', recentlyViewedData);

    let addedAny = false;

    // Prefetch variations for top 3 recently viewed (non-blocking for others)
    const prefetchIds = recentlyViewedData.slice(0, 3).map(item => item.id).filter(Boolean);
    if (prefetchIds.length > 0) {
        ensureBatch(prefetchIds).catch(() => {});
    }

    // Add each recently viewed product (silent mode - no alerts)
    // Use internal function to avoid multiple re-renders
    for (const product of recentlyViewedData) {
        if (product && product.id) {
            // Don't auto-add if already in selection
            if (!selectedCourses.find(course => course.product_id === product.id)) {
                await addCourseToSelectionInternal(product.id, 0, true); // true = silent mode
                addedAny = true;
            }
        }
    }

    // If we added any products, manage free courses and render once
    if (addedAny) {
        await autoManageFreeBasicCourse();
        renderSelectedCourses();
        updateTotals();
        saveCoursesToStorage();
    }
}

/**
 * Auto add from query string
 */
export async function autoAddProductFromURL(autoAddData) {
    const productId = parseInt(autoAddData.product_id, 10);
    const variationId = parseInt(autoAddData.variation_id, 10) || 0;

    if (!productId) {
        return;
    }

    await addCourseToSelection(productId, variationId);

    if (window.history && window.history.replaceState) {
        const cleanUrl = window.location.href.split('?')[0];
        window.history.replaceState({}, document.title, cleanUrl);
    }
}
